import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Input, Table, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useFirestoreConnect } from "react-redux-firebase";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { colors } from "../../../../../utils/theme";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const QrScansTable = () => {
  const { t } = useTranslation();
  const [filteredScans, setFilteredScans] = useState([]);
  const [filteredAggregatedScans, setFilteredAggregatedScans] = useState([]);
  const [productScans, setProductScans] = useState([]);
  const products = useSelector((state) => state.firestoreReducer.ordered.products);

  useFirestoreConnect([
    {
      collection: "qrScans",
      orderBy: ["timestamp", "desc"],
      storeAs: "qrScans",
    },
  ]);

  const qrScans = useSelector((state) => state.firestoreReducer.ordered.qrScans);
  const firestoreState = useSelector((state) => state.firestoreReducer);

  console.log("=== QR Scans Debug ===");
  console.log("qrScans:", qrScans);
  console.log("qrScans length:", qrScans?.length);
  console.log("products:", products?.length, "products loaded");
  console.log("Full firestore ordered keys:", Object.keys(firestoreState.ordered || {}));
  console.log("Full firestore data keys:", Object.keys(firestoreState.data || {}));

  useEffect(() => {
    console.log("useEffect triggered");
    console.log("qrScans exists:", !!qrScans);
    console.log("products exists:", !!products);

    if (qrScans && products) {
      console.log("Processing scans...");
      const scansWithProductInfo = qrScans.map((scan) => {
        console.log("Processing scan:", scan);
        const product = products.find((p) => p.id === scan.productId);
        console.log("Found product:", product?.name);
        return {
          ...scan,
          productName: product ? `${product.name} (${product.code})` : scan.productId,
          productCode: product?.code || "",
        };
      });
      console.log("Processed scans:", scansWithProductInfo.length);
      setProductScans(scansWithProductInfo);
      setFilteredScans(scansWithProductInfo);
    } else {
      console.log("Waiting for data - qrScans:", !!qrScans, "products:", !!products);
    }
  }, [qrScans, products]);

  const handleSearchAggregated = (value, aggregatedData) => {
    if (value === "") {
      setFilteredAggregatedScans(aggregatedData);
      return;
    }
    const filtered = aggregatedData.filter(
      (scan) =>
        scan.productName?.toLowerCase().includes(value.toLowerCase()) ||
        scan.productCode?.toLowerCase().includes(value.toLowerCase()) ||
        scan.productId?.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredAggregatedScans(filtered);
  };

  const handleSearchRecent = (value) => {
    if (value === "") {
      setFilteredScans(productScans);
      return;
    }
    const filtered = productScans.filter(
      (scan) =>
        scan.productName?.toLowerCase().includes(value.toLowerCase()) ||
        scan.productCode?.toLowerCase().includes(value.toLowerCase()) ||
        scan.productId?.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredScans(filtered);
  };

  const aggregateScans = () => {
    const aggregated = {};
    productScans.forEach((scan) => {
      if (!aggregated[scan.productId]) {
        aggregated[scan.productId] = {
          productId: scan.productId,
          productName: scan.productName,
          productCode: scan.productCode,
          scanCount: 0,
          lastScanned: scan.timestamp,
        };
      }
      aggregated[scan.productId].scanCount += 1;
      if (
        scan.timestamp &&
        (!aggregated[scan.productId].lastScanned || scan.timestamp > aggregated[scan.productId].lastScanned)
      ) {
        aggregated[scan.productId].lastScanned = scan.timestamp;
      }
    });
    return Object.values(aggregated).sort((a, b) => b.scanCount - a.scanCount);
  };

  const prepareChartData = () => {
    const scansByDate = {};
    
    productScans.forEach((scan) => {
      const date = dayjs.unix(scan.timestamp).format("DD-MM-YYYY");
      if (!scansByDate[date]) {
        scansByDate[date] = 0;
      }
      scansByDate[date] += 1;
    });

    const sortedDates = Object.keys(scansByDate).sort((a, b) => {
      return dayjs(a, "DD-MM-YYYY").unix() - dayjs(b, "DD-MM-YYYY").unix();
    });

    return sortedDates.map((date) => ({
      date,
      scans: scansByDate[date],
    }));
  };

  const aggregatedData = aggregateScans();
  const chartData = prepareChartData();

  useEffect(() => {
    setFilteredAggregatedScans(aggregatedData);
  }, [productScans]);

  if (!qrScans) {
    return (
      <Card>
        <CardTitle>{t("qr_code_scans")}</CardTitle>
        <Spin />
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardTitle>{t("qr_code_scans")}</CardTitle>
        <StatsContainer>
          <StatCard>
            <StatValue>{productScans.length}</StatValue>
            <StatLabel>{t("total_scans")}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{aggregatedData.length}</StatValue>
            <StatLabel>{t("unique_products_scanned")}</StatLabel>
          </StatCard>
        </StatsContainer>

        <SectionTitle>{t("scans_over_time")}</SectionTitle>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="scans" stroke={colors.primary} strokeWidth={2} name={t("scans")} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <CardTitle>{t("scans_by_product")}</CardTitle>
        <Input
          style={{ width: "fit-content", marginBottom: 16 }}
          placeholder={t("search")}
          onChange={(e) => handleSearchAggregated(e.target.value, aggregatedData)}
        />
        <div style={{ overflowX: "auto" }}>
          <Table
            columns={[
              {
                title: t("product"),
                dataIndex: "productName",
                key: "productName",
                render: (text) => <span>{text}</span>,
              },
              {
                title: t("scan_count"),
                dataIndex: "scanCount",
                key: "scanCount",
                sorter: (a, b) => a.scanCount - b.scanCount,
                defaultSortOrder: "descend",
                render: (text) => <span>{text}</span>,
              },
              {
                title: t("last_scanned"),
                dataIndex: "lastScanned",
                key: "lastScanned",
                sorter: (a, b) => {
                  if (!a.lastScanned || !b.lastScanned) return 0;
                  return a.lastScanned - b.lastScanned;
                },
                render: (timestamp) => <span>{timestamp ? dayjs.unix(timestamp).format("DD-MM-YYYY HH:mm") : "-"}</span>,
              },
            ]}
            dataSource={filteredAggregatedScans}
            rowKey="productId"
            pagination={{ pageSize: 20 }}
          />
        </div>
      </Card>

      <Card>
        <CardTitle>{t("recent_scans")}</CardTitle>
        <Input
          style={{ width: "fit-content", marginBottom: 16 }}
          placeholder={t("search")}
          onChange={(e) => handleSearchRecent(e.target.value)}
        />
        <div style={{ overflowX: "auto" }}>
          <Table
            columns={[
              {
                title: t("product"),
                dataIndex: "productName",
                key: "productName",
                render: (text) => <span>{text}</span>,
              },
              {
                title: t("timestamp"),
                dataIndex: "timestamp",
                key: "timestamp",
                render: (timestamp) => (
                  <span>{timestamp ? dayjs.unix(timestamp).format("DD-MM-YYYY HH:mm:ss") : "-"}</span>
                ),
              },
              {
                title: t("scanned_url"),
                dataIndex: "scannedUrl",
                key: "scannedUrl",
                render: (url) => <span style={{ fontSize: "0.875rem" }}>{url}</span>,
              },
            ]}
            dataSource={filteredScans}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </div>
      </Card>
    </>
  );
};

const Card = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06);
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background-color: #f9fafb;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${colors.primary};
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

export default QrScansTable;
